import { Fragment, useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Typography,
  Box,
} from '@mui/material';

import {
  distinct,
  floatVal,
  intVal,
  Nullable,
} from '@jamesgmarks/utilities';

import { ISubscriptions } from '@llws/lift-entity-interfaces';

import { Charge } from '../../../../../entities/hydra';
import { Link } from 'react-router-dom';
import MultipleSelectCheckmarks from '../../parts/mui/MultipleSelectCheckmarks';
import { StyledTableCell, StyledTableRow } from '../../parts/mui/StyledTables';
import { useAppSelector } from '../../../redux/hooks';
import { toDollarAmount } from '../../../app-utils';
import { EHydraState } from '../../../entity-interfaces/IInvoice';
import CreditSubscriptionSelection from './CreditSubscriptionSelection';
import { filterCharges, filterInvoiceOptions } from './chargeSearchUtils';

interface CreditModalProps {
  searchClientId: number;
  onChargeSelected: (charges: Charge[], subscription: ISubscriptions, invoiceIds: number[]) => void;
  onHide?: () => void;
  show: boolean;
}

export const CreditModal = ({
  searchClientId,
  onChargeSelected,
  onHide,
  show,
}: CreditModalProps) => {
  const charges = useAppSelector((state) => state.lookups.charges);

  const [ selectedSubscription, setSelectedSubscription ] = useState<Nullable<ISubscriptions>>(null);

  const [ selectedCharges, setSelectedCharges ] = useState<Array<{ charge: Charge, invoiceIds: number[] }>>([]);

  const [ selectedChargeRowIndex, setSelectedChargeRowIndex ] = useState<Nullable<number>>(null);
  const [ selectedChargeInvoiceIds, setSelectedChargeInvoiceIds ] = useState<number[]>([]);
  const [ searchTerm, setSearchTerm ] = useState<string>('');

  const filteredCharges = filterCharges(charges, searchTerm);

  const addChargeToSelection = (charge: Charge, invoiceIds: number[]) => {
    // Check if charge is already selected
    const isAlreadySelected = selectedCharges.some(sc => sc.charge.id === charge.id);

    if (!isAlreadySelected) {
      setSelectedCharges(prev => [...prev, { charge, invoiceIds }]);
    }

    // Reset the current selection UI
    setSelectedChargeRowIndex(null);
    setSelectedChargeInvoiceIds([]);
  };

  const removeChargeFromSelection = (chargeId: number) => {
    setSelectedCharges(prev => prev.filter(sc => sc.charge.id !== chargeId));
  };

  const handleConfirmSelection = () => {
    if (selectedCharges.length > 0 && selectedSubscription) {
      const charges = selectedCharges.map(sc => sc.charge);
      const allInvoiceIds = selectedCharges.flatMap(sc => sc.invoiceIds);

      onChargeSelected(charges, selectedSubscription, allInvoiceIds);

      // Reset state
      setSelectedSubscription(null);
      setSelectedCharges([]);
      setSelectedChargeRowIndex(null);
      setSelectedChargeInvoiceIds([]);
    }
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth='lg'
      open={show}
    >
      <DialogTitle sx={{ fontSize: '1.5rem' }}>
        { selectedSubscription ? 'Choose a Charge to Credit' : 'Choose a Subscription' }
      </DialogTitle>
      <DialogContent>
        {
          selectedSubscription
            ?
            <>
              <Grid container>
                <Grid item xs={8}>
                  <u>
                    <b>Selected Subscription</b>: {selectedSubscription.id} - {selectedSubscription.invoiceDescription}
                  </u>
                  {selectedCharges.length > 0 && (
                    <div style={{ marginTop: '8px', color: 'green' }}>
                      <strong>{selectedCharges.length} charge(s) selected</strong>
                    </div>
                  )}
                </Grid>
                <Grid item xs={4} textAlign='right'>
                  <Button
                    color='warning'
                    onClick={() => {
                      setSelectedSubscription(null);
                      setSelectedChargeRowIndex(null);
                      setSelectedCharges([]);
                    }}
                    variant='contained'
                  >View Subscriptions
                  </Button>
                </Grid>
              </Grid>
              {
                charges.length === 0
                  ?
                  <Typography mt={3} variant='h6'><b><u>No Charges Available for This Subscription</u></b></Typography>
                  :
                  <>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <TextField
                        label="Search by charge ID or invoice number"
                        placeholder="Enter charge ID or invoice number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Box>
                    {/* <u><p>Choose a Charge to Credit:</p></u> */}
                    <TableContainer
                      sx={{
                        mt: 2,
                        maxHeight: '50vh',
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Table stickyHeader sx={{ width: '100%', textAlign: 'center' }}>
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell>Charge ID</StyledTableCell>
                            <StyledTableCell>Year</StyledTableCell>
                            <StyledTableCell>Month</StyledTableCell>
                            <StyledTableCell>Invoice Count</StyledTableCell>
                            <StyledTableCell>Subtotal</StyledTableCell>
                            <StyledTableCell>Tax</StyledTableCell>
                            <StyledTableCell>Total</StyledTableCell>
                            <StyledTableCell>&nbsp;</StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {
                            filteredCharges.map(
                              (charge, displayIndex) => {
                                const i = charges.findIndex(c => c.id === charge.id);
                                const invoiceIds = distinct(
                                  (charge.chargeHasTaxes ?? [])
                                    .filter((cht) => (
                                      ![EHydraState.deleted, EHydraState.unsent].includes(cht.invoice.hydraState)
                                    ))
                                    .map((cht) => cht.invoiceId),
                                );

                                const invoiceCount = invoiceIds.length;

                                const invoiceNumbers = invoiceIds.map((id) => {
                                  const gsht = charge.chargeHasTaxes.find(
                                    (cht) => intVal(cht.invoiceId) === id,
                                  );
                                  return gsht?.invoice?.invoiceNumber ? gsht.invoice.invoiceNumber : `PV: ${id}`;
                                });

                                const options = invoiceIds
                                  .map((id, index) => ({ label: invoiceNumbers[index], value: `${id}` }))
                                  .sort((a, b) => a.label < b.label ? -1 : 1);

                                const filteredOptions = filterInvoiceOptions(options, searchTerm);

                                return (
                                  <Fragment key={charge.id ?? i}>
                                    <StyledTableRow hover={true} key={i}>
                                      <StyledTableCell>
                                        <Link
                                          to={`/charges/${charge.id}`}
                                          target="_blank"
                                        >
                                          {charge.id}
                                        </Link>
                                      </StyledTableCell>
                                      <StyledTableCell>{charge.year}</StyledTableCell>
                                      <StyledTableCell>{charge.month}</StyledTableCell>
                                      <StyledTableCell> {invoiceCount} </StyledTableCell>
                                      <StyledTableCell>
                                        {
                                          toDollarAmount(
                                            (floatVal(charge.proratedSubtotal) ?? 0) +
                                            (floatVal(charge.discountAmount) ?? 0),
                                          )
                                        }
                                      </StyledTableCell>
                                      <StyledTableCell>{toDollarAmount(floatVal(charge.taxAmount) ?? 0)}</StyledTableCell>
                                      <StyledTableCell>{
                                        toDollarAmount(
                                          (floatVal(charge.proratedSubtotal) ?? 0)
                                      + (floatVal(charge.discountAmount) ?? 0)
                                      + (floatVal(charge.taxAmount) ?? 0),
                                        )
                                      }</StyledTableCell>
                                      <StyledTableCell>
                                        {selectedCharges.some(sc => sc.charge.id === charge.id) ? (
                                          <Button
                                            color="success"
                                            size="small"
                                            variant='contained'
                                            onClick={() => {
                                              if (charge.id) {
                                                removeChargeFromSelection(charge.id);
                                              }
                                            }}
                                          >
                                            Added ✓
                                          </Button>
                                        ) : (
                                          <Button
                                            color={i === selectedChargeRowIndex && selectedChargeInvoiceIds.length > 0
                                              ? 'success'
                                              : 'primary'}
                                            disabled={!invoiceCount
                                          || ( invoiceCount > 1
                                            && selectedChargeInvoiceIds.length === 0
                                            && selectedChargeRowIndex === i
                                          )}
                                            onClick={(e) => {
                                              if (selectedChargeRowIndex !== i && invoiceCount !== 1) {
                                                setSelectedChargeInvoiceIds([]);
                                                setSelectedChargeRowIndex(i);
                                                return;
                                              }

                                              if (invoiceCount === 1) {
                                                addChargeToSelection(charge, invoiceIds);
                                              }

                                              if (selectedChargeInvoiceIds.length > 0) {
                                                addChargeToSelection(charge, selectedChargeInvoiceIds);
                                              }
                                            }}
                                            size="small"
                                            variant='contained'
                                          >
                                            {
                                              i === selectedChargeRowIndex && selectedChargeInvoiceIds.length > 0
                                              // eslint-disable-next-line max-len
                                                ? `Add ${selectedChargeInvoiceIds.length} Invoice${selectedChargeInvoiceIds.length > 1 ? 's' : ''}`
                                                : i === selectedChargeRowIndex
                                                  ? 'Select Invoices'
                                                  : 'Select'
                                            }
                                          </Button>
                                        )}
                                      </StyledTableCell>
                                    </StyledTableRow>
                                    {
                                      selectedChargeRowIndex === i &&
                                  <StyledTableRow>
                                    <StyledTableCell align='right' colSpan={8}>
                                      <MultipleSelectCheckmarks
                                        options={filteredOptions}
                                        inputLabelText='Invoices'
                                        setSelectedValues={(stringValues) => {
                                          // Convert strings to numbers at the UI boundary
                                          const values = Array.isArray(stringValues) ? stringValues : [];
                                          const numberValues = values.map(val => intVal(val));
                                          setSelectedChargeInvoiceIds(numberValues);
                                        }}
                                        onSelectionCommitted={(stringValues) => {
                                          const numberValues = stringValues.map(val => intVal(val));
                                          if (numberValues.length > 0) {
                                            addChargeToSelection(charge, numberValues);
                                          }
                                        }}
                                      />
                                    </StyledTableCell>
                                  </StyledTableRow>
                                    }
                                  </Fragment>
                                );
                              },
                            )
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {selectedCharges.length > 0 && (
                      <Grid container sx={{ mt: 2 }} justifyContent="center">
                        <Button
                          color="primary"
                          variant="contained"
                          size="large"
                          onClick={handleConfirmSelection}
                        >
                          Create Credit for {selectedCharges.length} Charge{selectedCharges.length > 1 ? 's' : ''}
                        </Button>
                      </Grid>
                    )}
                  </>
              }
            </>
            :
            <>
              <CreditSubscriptionSelection
                searchClientId={searchClientId}
                setSelectedSubscription={setSelectedSubscription}
              />
            </>
        }</DialogContent>
      <DialogActions>
        <Button color="secondary"
          onClick={(e) => {
            setSelectedSubscription(null);
            setSelectedCharges([]);
            onHide?.();
          }}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
